import { type ICSVExport } from '@principle-theorem/ng-shared';
import { ValueFormatter } from '@principle-theorem/principle-core';
import { DataPointValue } from '@principle-theorem/principle-core/interfaces';
import {
  ReportBuilderDataSource,
  ReportBuilderDataSourceInstance,
  TABLE_GROUP_KEY,
} from '@principle-theorem/reporting';
import {
  ICanBeChartedProperty,
  IGroupBySectionOption,
} from '@principle-theorem/reporting/interfaces';
import { compact, get } from 'lodash';
import { getD3NumberFormatter } from '../../../../core/chart-builders/dc-chart-builders/dc-single-value-chart-builder';
import { IChartDisplay } from '../report-builder-chart/report-builder-chart-display';

export interface ICSVColumn {
  id: string;
  label: string;
  metadata: ICanBeChartedProperty['metadata'];
}

export class ReportBuilderTableChartCSV
  implements ICSVExport<Record<string, unknown>, Record<string, unknown>>
{
  defaultFileName = 'report-builder-table-chart';
  headers: string[] = [];
  columns: ICSVColumn[];

  constructor(
    private _selectedColumns: ICSVColumn[],
    private _groupBy?: IGroupBySectionOption
  ) {
    this.columns = this._selectedColumns;
    this.headers = this.columns.map((column) => column.label);
  }

  translate(
    records: Record<string, unknown>[]
  ): Record<string, unknown>[] | Promise<Record<string, unknown>[]> {
    return records.map((record) => this._translateRecord(record));
  }

  private _translateRecord(
    record: Record<string, unknown>
  ): Record<string, unknown> {
    return this.columns.reduce(
      (acc: Record<string, unknown>, column) => ({
        ...acc,
        [column.id]: this._getValue(record, column),
      }),
      {}
    );
  }

  private _getValue(
    record: Record<string, unknown>,
    column: ICSVColumn
  ): string {
    const propertyKey =
      column.label === this._groupBy?.measure.metadata.label
        ? TABLE_GROUP_KEY
        : column.label;
    const dataPoint = get(record, propertyKey) as DataPointValue;

    if (dataPoint === undefined) {
      return '';
    }

    const canFilterDataPoint = !getD3NumberFormatter(
      column.metadata.formatter,
      column.metadata.formatterValue
    );

    if (canFilterDataPoint) {
      return dataPoint.toString();
    }

    return ValueFormatter.format(
      dataPoint,
      column.metadata.formatter,
      column.metadata.formatterValue
    );
  }
}

export class ReportBuilderTableChart {
  static getCSVData(display: IChartDisplay): object[] {
    return display.tableDisplay?.chart.data ?? [];
  }

  static getCSVTranslator$(
    display: IChartDisplay,
    dataSource?: ReportBuilderDataSourceInstance
  ): ReportBuilderTableChartCSV | undefined {
    if (!dataSource) {
      return;
    }

    const groupBy = dataSource.groupByOptions
      .flatMap((section) => section.options)
      .find((item) => item.measure.metadata.id === display.chart.groupBy);

    if (!groupBy) {
      return;
    }

    const dataPoints = (display.tableDisplay?.columns ?? []).map((column) => {
      const metadata = ReportBuilderDataSource.allColumns(dataSource).find(
        (item) => item.measure.metadata.id === column.id
      )?.measure.metadata;

      if (!metadata) {
        return;
      }

      return {
        label: column.label,
        uid: column.uid,
        id: column.uid,
        metadata,
      };
    });

    const columns: ICSVColumn[] = compact([
      {
        id: groupBy?.measure.metadata.id ?? '',
        label: groupBy?.measure.metadata.label ?? '',
        metadata: groupBy?.measure?.metadata,
      },
      ...dataPoints,
    ]);

    return new ReportBuilderTableChartCSV(columns, groupBy);
  }
}
